import { useState } from 'react';
import type { RunningCourse } from '../api/types';
import { TextField } from './Chrome';
import { batchesLabel } from './courseLabel';

/** CourseRow in LectureEntryScreen.kt. */
function CourseRow({
  course,
  selected,
  disabled,
  registered,
  onClick,
}: {
  course: RunningCourse;
  selected: boolean;
  disabled: boolean;
  registered?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`course${selected ? ' course--selected' : ''}`}
      disabled={disabled}
      aria-pressed={selected}
      onClick={onClick}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="course__code">{course.code}</span>
        {registered && <span className="course__badge">Registered</span>}
        <br />
        <span className="course__meta">{course.name}</span>
        <br />
        <span className="course__meta course__meta--batch">{batchesLabel(course.batches)}</span>
      </span>
      <span className="course__tick" aria-hidden="true">
        {selected ? '✓' : ''}
      </span>
    </button>
  );
}

/**
 * Search-as-you-type picker over currently-running sessions only — the
 * campus-wide list has no enrolment filter, so this keeps it usable when many
 * courses are live at once instead of scrolling a plain list.
 *
 * Courses the student registered ahead of time (see CourseRegistrationScreen)
 * are pinned above the field at rest, so a returning student never has to
 * type to find their own lecture. Typing anything drops the pinned list and
 * falls back to plain search, same as before registration existed.
 */
export function CoursePicker({
  courses,
  selectedId,
  disabled,
  registeredIds,
  onSelect,
}: {
  courses: RunningCourse[];
  selectedId: string | null;
  disabled: boolean;
  registeredIds: Set<string>;
  onSelect: (courseId: string | null) => void;
}) {
  const [query, setQuery] = useState('');
  const selected = courses.find((c) => c._id === selectedId);

  if (selected) {
    return (
      <CourseRow
        course={selected}
        selected
        disabled={disabled}
        onClick={() => {
          onSelect(null);
          setQuery('');
        }}
      />
    );
  }

  const trimmed = query.trim();
  const pinned = trimmed ? [] : courses.filter((c) => registeredIds.has(c._id));
  const matches = trimmed
    ? courses.filter((c) =>
        `${c.code} ${c.name} ${batchesLabel(c.batches)}`.toLowerCase().includes(trimmed.toLowerCase()),
      )
    : [];

  return (
    <>
      <TextField
        label="Search your course"
        value={query}
        onChange={setQuery}
        disabled={disabled}
        placeholder="Course code or name…"
        type="search"
        inputMode="search"
      />
      {!trimmed && pinned.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {pinned.map((course) => (
            <CourseRow
              key={course._id}
              course={course}
              selected={false}
              disabled={disabled}
              registered
              onClick={() => {
                onSelect(course._id);
                setQuery('');
              }}
            />
          ))}
        </div>
      )}
      {/* Otherwise a true dropdown: nothing shows until the student actually
          searches, rather than dumping the whole running-courses list under
          the field. */}
      {trimmed &&
        (matches.length === 0 ? (
          <p className="course__none">No running lecture matches “{trimmed}”.</p>
        ) : (
          <div style={{ marginTop: 8 }}>
            {matches.slice(0, 20).map((course) => (
              <CourseRow
                key={course._id}
                course={course}
                selected={false}
                disabled={disabled}
                onClick={() => {
                  onSelect(course._id);
                  setQuery('');
                }}
              />
            ))}
          </div>
        ))}
    </>
  );
}
